"use client";

import { CheckCircle, Copy, ExternalLink, RefreshCcw, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { DOMAIN_STATUS_ENDPOINT_INSIDE } from "@/config";
import {
  ACTIVE_SUBDOMAINS_SUPPLIER,
  ACTIVE_SUBDOMAINS_TENANT,
  type ActiveSubdomain,
} from "@/lib/subdomainRegistry";

export interface CDomainVerificationProps {
  /** The bare domain registered to the tenant/supplier, e.g. "shebaa247.com" */
  domain: string;
  /** Whether this domain belongs to a supplier (different VPS) */
  tenantType?: "tenant" | "supplier";
  /** Card background variant */
  variant?: "amber" | "white";
}

interface DomainStatus {
  domain: string;
  expectedIp: string;
  dnsOk: boolean;
  dnsResolvedTo: string[];
  httpsOk: boolean;
  httpsStatus: number | null;
  sslActive: boolean;
  sslIssuer: string | null;
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      title="Copy"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? (
        <CheckCircle className="size-3 text-green-500" />
      ) : (
        <Copy className="size-3" />
      )}
      <span>{copied ? "Copied!" : "Copy"}</span>
    </button>
  );
}

function StatusRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <CheckCircle className="size-4 text-emerald-600" />
      ) : (
        <XCircle className="size-4 text-amber-500" />
      )}
      <span className={ok ? "text-emerald-700 dark:text-emerald-300" : "text-muted-foreground"}>
        {label}
      </span>
    </div>
  );
}

function CDomainVerification({
  domain,
  tenantType = "tenant",
  variant = "white",
}: CDomainVerificationProps) {
  const isSupplier = tenantType === "supplier";
  const vpsIp = process.env.NEXT_PUBLIC_VPS_IP || "";
  const subdomains: readonly ActiveSubdomain[] = isSupplier
    ? ACTIVE_SUBDOMAINS_SUPPLIER
    : ACTIVE_SUBDOMAINS_TENANT;

  const [status, setStatus] = useState<DomainStatus | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!domain) return;
    setLoading(true);
    try {
      const url = `${DOMAIN_STATUS_ENDPOINT_INSIDE}?domain=${encodeURIComponent(
        domain,
      )}&type=${tenantType}`;
      const res = await fetch(url, { credentials: "same-origin" });
      const json = await res.json();
      if (json?.success) setStatus(json.data as DomainStatus);
    } catch (e) {
      console.error("[CDomainVerification] status fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [domain, tenantType]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 15000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const cardClass =
    variant === "amber"
      ? "rounded-lg border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-800/60 dark:bg-amber-950/20"
      : "bg-white dark:bg-slate-900 rounded-lg p-5 border border-border/50";

  const allReady = !!status && status.dnsOk && status.httpsOk && status.sslActive;

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          Domain Setup
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            onClick={fetchStatus}
            disabled={loading}
          >
            <RefreshCcw className={`size-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <a
            href={`https://${domain}`}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-1"
          >
            Test <ExternalLink className="size-3" />
          </a>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm mb-4">
        <span className="text-slate-600 dark:text-slate-400">Domain</span>
        <span className="font-semibold text-indigo-600 dark:text-indigo-400">
          {domain}
        </span>
      </div>

      {!vpsIp && (
        <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
          NEXT_PUBLIC_VPS_IP is not set — DNS records below are blank. Set it
          in env to surface the right IP.
        </p>
      )}

      {/* DNS instructions */}
      <div className="space-y-2.5 border-t border-border/50 pt-3 mb-4">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Add these DNS records at your domain registrar:
        </p>

        <div className="rounded-md border bg-card overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Type</th>
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Value</th>
                <th className="px-3 py-1.5 text-right font-medium text-muted-foreground w-10" />
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="px-3 py-2">
                  <span className="rounded bg-blue-100 px-1.5 py-0.5 font-medium text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                    A
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-foreground">@</td>
                <td className="px-3 py-2 font-mono text-foreground break-all">{vpsIp}</td>
                <td className="px-3 py-2 text-right">
                  {vpsIp && <CopyBtn text={vpsIp} />}
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2">
                  <span className="rounded bg-purple-100 px-1.5 py-0.5 font-medium text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
                    A
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-foreground">*</td>
                <td className="px-3 py-2 font-mono text-foreground break-all">{vpsIp}</td>
                <td className="px-3 py-2 text-right">
                  {vpsIp && <CopyBtn text={vpsIp} />}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed">
          The wildcard <span className="font-mono">*</span> A record covers
          all subdomains (www, staff, etc.) automatically. SSL is provisioned
          by Let&apos;s Encrypt on the first HTTPS request (10–30 seconds).
        </p>
      </div>

      {/* Active subdomains */}
      <div className="space-y-1.5 border-t border-border/50 pt-3 mb-4">
        <p className="text-xs font-medium text-foreground">
          Subdomains active automatically:
        </p>
        <ul className="text-xs space-y-0.5">
          {subdomains.map((s) => {
            const fullHost = s.host ? `${s.host}.${domain}` : domain;
            return (
              <li key={`${s.host}-${s.service}`} className="flex justify-between">
                <span className="font-mono text-foreground">
                  {fullHost}
                  {s.pathPrefix ?? ""}
                </span>
                <span className="text-muted-foreground">{s.service}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Live status */}
      <div className="space-y-2 border-t border-border/50 pt-3">
        <p className="text-xs font-medium text-foreground">Status</p>
        <StatusRow
          label={
            status?.dnsOk
              ? `DNS resolved to ${vpsIp}`
              : status?.dnsResolvedTo?.length
                ? `DNS resolves to ${status.dnsResolvedTo.join(", ")} (expected ${vpsIp})`
                : "DNS not resolving yet"
          }
          ok={!!status?.dnsOk}
        />
        <StatusRow
          label={status?.httpsOk ? "HTTPS reachable" : "HTTPS not reachable yet"}
          ok={!!status?.httpsOk}
        />
        <StatusRow
          label={
            status?.sslActive
              ? `SSL active (issued by ${status.sslIssuer || "Let's Encrypt"})`
              : "SSL certificate not active yet"
          }
          ok={!!status?.sslActive}
        />
        {allReady && (
          <div className="flex items-center gap-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-3 py-2 mt-2">
            <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <p className="text-xs text-emerald-700 dark:text-emerald-300">
              Domain live and serving over HTTPS
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CDomainVerification;
